import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';
import { Shield, Target, Heart, Lightbulb, Users, FileCheck, Building2, Scale, ArrowRight, Quote } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const stagger = (i) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.4, delay: i * 0.1 } });

const values = [
  { icon: Shield, title: 'Compliance First', desc: 'Every feature starts with one question: would this have saved time and stress on audit day?' },
  { icon: Target, title: 'Simplicity', desc: 'Complex regulations deserve simple tools. No legal jargon. No complicated workflows.' },
  { icon: Heart, title: 'Built for Real People', desc: 'Designed for the HR manager juggling fifteen responsibilities, not a dedicated compliance department.' },
  { icon: Lightbulb, title: 'Continuous Innovation', desc: 'We ship updates driven by real customer feedback and evolving DOL requirements.' },
];

const stats = [
  { label: 'Companies Trusting PAFHub', value: 'Hundreds' },
  { label: 'PAFs Processed', value: 'Thousands' },
  { label: 'Audit Confidence', value: '100%' },
];

const audiences = [
  { icon: Users, title: 'HR Teams', desc: 'Who need simplicity and speed in managing employee compliance files.' },
  { icon: Scale, title: 'Compliance Officers', desc: 'Who need accuracy and auditability across every PAF.' },
  { icon: Building2, title: 'Immigration Attorneys', desc: 'Who need visibility across their entire client base in one place.' },
];

export default function AboutPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Our Story"
        description="PAFHub was born from a real employer's frustration with H-1B Public Access File compliance. Read how Kishore Kandula turned audit anxiety into the platform trusted by hundreds of companies."
        path="/about"
        keywords="PAFHub story, Kishore Kandula, H-1B compliance founder, PAF management origin, immigration compliance software"
      />

      {/* SEO-only H1 (visually hidden, fully accessible to search engines & screen readers) */}
      <h1 className="sr-only">PAFHub — Simplify H-1B, H-1B1 & E-3 Public Access Files(PAF) Compliance</h1>

      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50 relative overflow-hidden">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-indigo-200/15 rounded-full blur-[100px]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">Our Story</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-6 leading-[1.15]">
              We Didn't Set Out to Build a<br />Software Company. We Set Out to<br />
              <span className="bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">Solve a Problem We Lived Every Day.</span>
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto leading-relaxed">
              PAFHub wasn't born in a boardroom or a venture capital pitch deck. It was born out of genuine frustration — the kind that comes from sitting across a table during a Department of Labor audit, scrambling through folders.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Origin — Kishore's Story */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 bg-indigo-600 rounded-full" />
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight">The Problem Nobody Talks About</h2>
            </div>
            <div className="space-y-5 text-[#475569] text-base leading-[1.8]">
              <p>
                As an employer sponsoring H-1B workers, <strong className="text-[#0F172A]">Kishore Kandula</strong> understood the value that skilled international talent brought to his company. He navigated the visa process, worked with attorneys, filed Labor Condition Applications (LCAs), and did everything he was supposed to do.
              </p>
              <p>
                But then came the <strong className="text-[#0F172A]">Public Access File</strong>.
              </p>
              <p>
                On paper, it sounds simple — maintain a file for each H-1B, H-1B1, or E-3 worker with specific documents, make it available within one business day of filing the LCA, and keep it updated throughout the employment period. Simple enough, right?
              </p>
              <p className="text-[#0F172A] font-medium italic">
                In practice, it was anything but.
              </p>
              <p>
                There were spreadsheets tracking which documents existed for which employees. There were email chains asking HR for signed wage memos. There were filing cabinets with folders that hadn't been touched in months. And every time a new LCA was filed, the process started all over again — manually, inconsistently, and anxiously.
              </p>
              <p>
                When the DOL came knocking, the scramble was real. And Kishore knew his company wasn't alone. Talking to peers in HR circles, he heard the same story repeated everywhere — <strong className="text-[#0F172A]">missing documents, missed deadlines, inconsistent file structures</strong>, and the constant low-grade anxiety of not knowing if you'd pass an audit.
              </p>
              <p>
                The irony? These weren't reckless employers. These were well-intentioned companies trying their best to comply, let down by a process stuck decades in the past.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Question */}
      <section className="py-16 lg:py-24 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.6 }} className="text-center">
            <Quote className="w-10 h-10 text-indigo-400/40 mx-auto mb-6" strokeWidth={1.5} />
            <blockquote className="text-2xl lg:text-3xl font-bold text-white leading-snug mb-6">
              "Why is this still being done with spreadsheets and filing cabinets?"
            </blockquote>
            <p className="text-indigo-200/70 text-base leading-relaxed max-w-xl mx-auto">
              We track packages in real time. We manage entire supply chains through software. We file taxes online in minutes. Yet one of the most compliance-critical HR obligations for thousands of U.S. employers was still managed with manila folders and color-coded Excel sheets.
            </p>
            <p className="text-indigo-300 font-semibold mt-6 text-sm">— Kishore Kandula, Founder</p>
          </motion.div>
        </div>
      </section>

      {/* Building the Platform */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 bg-indigo-600 rounded-full" />
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight">Building the Platform We Wished Existed</h2>
            </div>
            <div className="space-y-5 text-[#475569] text-base leading-[1.8]">
              <p>
                Kishore partnered with engineers from top tech companies who shared his belief that compliance software didn't have to be clunky, expensive, or built for lawyers alone. Together, they asked a different question: <em>what would PAF management look like if it were designed for HR teams and employers first?</em>
              </p>
              <p>The answer became <strong className="text-[#0F172A]">PAFHub</strong>.</p>
            </div>

            {/* Feature highlights */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: FileCheck, text: 'Upload your LCA and supporting documents in minutes' },
                { icon: Target, text: 'Track the status of every PAF across every employee' },
                { icon: Shield, text: 'Get alerted before deadlines slip through the cracks' },
                { icon: Scale, text: 'Be audit-ready before the DOL finishes the sentence' },
              ].map((item, i) => (
                <motion.div key={i} {...stagger(i)} className="flex items-start gap-3 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/60">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon className="w-4.5 h-4.5 text-indigo-600" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-[#334155] leading-relaxed font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 space-y-5 text-[#475569] text-base leading-[1.8]">
              <p>
                Every feature was built by asking one question: <strong className="text-[#0F172A]">would this have saved Kishore time and stress on the day of that audit?</strong> If the answer was yes, it made it in.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-20 lg:py-28 bg-[#F8FAFF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-14">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-3">Built for the People Doing the Real Work</h2>
            <p className="text-base text-[#64748B] max-w-xl mx-auto leading-relaxed">
              We know your day doesn't revolve around immigration compliance. We built PAFHub so that it doesn't have to.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {audiences.map((a, i) => (
              <motion.div key={a.title} {...stagger(i)} className="text-center p-8 bg-white rounded-2xl border border-indigo-100/50 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-5">
                  <a.icon className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
                </div>
                <h4 className="text-base font-bold text-[#0F172A] mb-2">{a.title}</h4>
                <p className="text-sm text-[#64748B] leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where We Are Today — Stats */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-3">Where We Are Today</h2>
            <p className="text-base text-[#64748B] max-w-xl mx-auto leading-relaxed">
              What started as one employer's frustration has grown into a platform trusted by companies across the United States.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {stats.map((s, i) => (
              <motion.div key={s.label} {...stagger(i)} className="text-center p-6 bg-gradient-to-br from-indigo-50/80 to-sky-50/40 rounded-2xl border border-indigo-100/50">
                <p className="text-3xl lg:text-4xl font-bold text-indigo-600 mb-1">{s.value}</p>
                <p className="text-sm text-[#64748B] font-medium">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-20 lg:py-28 bg-[#F8FAFF]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-8 bg-indigo-600 rounded-full" />
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight">Our Promise</h2>
            </div>
            <div className="space-y-5 text-[#475569] text-base leading-[1.8]">
              <p>
                We will never forget where we came from — a real employer, facing a real problem, looking for a real solution that didn't exist yet.
              </p>
              <p>
                Every update we ship, every feature we build, and every support ticket we answer is guided by that origin story. Because at PAFHub, <strong className="text-[#0F172A]">compliance isn't just a product we sell. It's a problem we lived.</strong> And we built the solution we wished someone had built for us.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="text-center mb-14">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-3">What Guides Us</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title} {...stagger(i)} className="text-center p-6 bg-[#F8FAFF] rounded-2xl border border-indigo-100/50">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                  <v.icon className="w-6 h-6 text-indigo-600" strokeWidth={1.5} />
                </div>
                <h4 className="text-base font-bold text-[#0F172A] mb-2">{v.title}</h4>
                <p className="text-sm text-[#64748B] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white tracking-tight mb-4">
              H-1B Public Access File Compliance, Simplified.
            </h2>
            <p className="text-indigo-200/70 text-base mb-8 max-w-lg mx-auto">
              Trusted by HR teams, compliance officers, and immigration attorneys nationwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                data-testid="about-cta-get-started"
                className="inline-flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-8 py-3.5 rounded-xl transition-all shadow-lg shadow-indigo-500/25 hover:shadow-indigo-400/30 text-[15px]"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="about-cta-contact"
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold px-8 py-3.5 rounded-xl border border-white/20 transition-all text-[15px]"
              >
                Request a Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  );
}
